import {
  ellipsis,
  FontWeight, getTypography, rgba, ThemedFunctor, TypographyName,
} from '@doveit/bricks';
import { css } from 'styled-components';

export const base: ThemedFunctor = ({ theme }) => css`
  display: flex;
  position: relative;
  gap: ${theme.remSize[200]};
  align-items: stretch;
`;

export const side: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.remSize[150]};
`;

export const filmStrip = () => css`
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  flex-grow: 1;
`;

export const filters = () => css`
  flex-grow: 0;
  flex-shrink: 0;
`;

export const main: ThemedFunctor = ({ theme }) => css`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.remSize[200]};
`;

export const mainPreview = () => css`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const actions = () => css`
  position: relative;
  display: flex;
`;

export const mainImageWrapper: ThemedFunctor = ({ theme }) => css`
  background: ${theme.color.background.neutral.default.high};
  position: relative;
  user-select: none;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 3 / 2;
`;

export const bar: ThemedFunctor = ({ theme }) => css`
  display: flex;
  position: absolute;
  z-index: 2;
  right: 0;
  left: 0;
  padding: ${theme.remSize[200]};
  gap: ${theme.remSize[200]};
  align-items: center;
  justify-content: space-between;
`;

export const topBar: ThemedFunctor = ({ theme }) => css`
  ${bar({ theme })}
  top: 0;
`;

export const overlayText: ThemedFunctor = ({
  theme,
}) => css`
  ${ellipsis()}
  ${getTypography(theme, TypographyName.BODY, { fontWeight: FontWeight.MEDIUM })}
  color: ${theme.color.foreground.neutral.default.lowest};
  filter: drop-shadow(${theme.color.foreground.neutral.default.highest} 0 0 2px);
`;

export const fileName = () => css`
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-end;
  min-width: 0;

  /* Badge does not truncate its label: constrain it and ellipsize the inner text */
  & > * {
    max-width: 100%;
    min-width: 0;
  }

  & > * > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const bottomBar: ThemedFunctor = ({ theme }) => css`
  ${bar({ theme })}
  bottom: 0;
  min-height: ${theme.remSize[300]};
`;

export const navigationAction: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  top: 0;
  bottom: 0;
  background: red;
  z-index: 1;
  padding: 0 ${theme.remSize[200]};
  transition: background-color 0.5s ease, padding 0.5s ease;
  color: ${theme.palettes.neutral[800]};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    padding: 0 ${theme.remSize[300]};
    cursor: pointer;

    & > div {
      background-color: ${theme.color.background.neutral.default.highest};
    }
  }

  & > div {
    color: ${theme.color.foreground.neutral.default.lowest};
    background-color: ${rgba(theme.color.background.neutral.default.highest, 0.7)};

    &, svg {
      line-height: 0;
    }
  }
`;

export const navigationPrevAction: ThemedFunctor = ({ theme }) => css`
  ${navigationAction({ theme })}
  background: transparent;
  left: 0;
`;

export const navigationNextAction: ThemedFunctor = ({ theme }) => css`
  ${navigationAction({ theme })}
  background: transparent;
  right: 0;
`;

export const thumbnailsContainer = () => css`
  flex: 0 0 260px;
  position: relative;
`;

export const carouselContainer = () => css`
  position: relative;
  width: 100%;
`;

export const filmStripWrapper = () => css`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const filmStripFade = () => css`
  position: absolute;
  left: 0;
  right: 0;
  height: 40px;
  pointer-events: none;
  z-index: 2;
  transition: opacity 0.3s ease;
`;

export const filmStripFadeTop: ThemedFunctor = ({ theme }) => css`
  ${filmStripFade()}
  top: 0;
  background: linear-gradient(to bottom, ${theme.color.background.neutral.default.lowest}, transparent);
`;

export const filmStripFadeBottom: ThemedFunctor = ({ theme }) => css`
  ${filmStripFade()}
  bottom: 0;
  background: linear-gradient(to top, ${theme.color.background.neutral.default.lowest}, transparent);
`;
